import React, {useEffect, useRef, useState} from "react";
import {ICartItemPrice, IWishlistItem} from "../../../typescript-library";
import {useTranslation, Trans} from "react-i18next";
import {getShopId} from "../../lib/getShopId";
import {AddToCartButton} from "../../cart/AddToCartButton/AddToCartButtonPortal";
import {useUnsafeCartContext} from "../../cart/CartContextProvider/CartContextProvider";
import styles from "./WishlistItem.module.scss";
import {isoLocales} from "../../lib/payments/PaymentController";
import EnergyLabel from "../../energyLabel/EnergyLabel";
import LegalAttributes from "../LegalAttributes";
import Image from "../../ProductSeries/Image";
import {scrollInfoStorage} from "../Wishlist";
import {useAppDispatch} from "../../../store";
import {toggleAddItem} from "../../../store/slices/wishlishSlice";

const WishlistItem: React.FC<{item: IWishlistItem; index: number}> = ({item, index}) => {
  const {images, title, itemId, isActive, prices, url, itemClassId, attributes} = item.item;
  const context = useUnsafeCartContext();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const {t} = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  // is there an energy efficiency class present
  const energyEfficiency = attributes.find((attrib) => attrib.itemAttributeId === 100361);
  const energyLabel = images.find((image) => !!image.energyLabel)?.energyLabel || undefined;
  const wishingDateString = t("wishlist.dateRequest--wishingDate", {
    wishingDate: new Intl.DateTimeFormat(isoLocales[getShopId()].replace("_", "-")).format(new Date(item.modified)),
  });

  useEffect(() => {
    const scrollInfo = scrollInfoStorage.read("scrollInfo");
    if (scrollInfo && itemId === scrollInfo.itemId) {
      ref.current?.scrollIntoView({block: "center"});
      scrollInfoStorage.remove("scrollInfo");
    }
  }, []);

  const onRemoveClick = () => {
    setIsLoading(true);
    void dispatch(toggleAddItem(itemId));
  };

  const onClickItem = () => {
    scrollInfoStorage.write("scrollInfo", {itemId, index});
  };

  const priceToUse = prices.find((prc) => prc.quantity.from === 1) || prices[0];

  return (
    <div ref={ref} className={`${styles.wrapper} ${isActive ? "" : styles.inactive}`}>
      <div className={styles.itemThumb}>
        <a href={url} title={title} tabIndex={-1} onClick={onClickItem}>
          <Image item={item.item} />
        </a>
      </div>
      <div className={styles.details}>
        <a href={url} className={styles.title} onClick={onClickItem}>
          {title}
        </a>

        <div>
          <div className={styles.priceLabelWrapper}>
            {!!energyEfficiency && (
              <EnergyLabel energyClass={energyEfficiency.value ?? "G"} media={energyLabel} size="small" />
            )}
            <ItemPrice itmPrice={priceToUse} />
          </div>
          {/* itemClassId 6 means it is a gift voucher */}
          {itemClassId !== 6 && (
            <div className={styles.shippingInfo}>
              <span>
                {t("general.inclVat")}
                {", "}
              </span>
              <Trans
                i18nKey="general.exclShipping"
                ns="translations"
                components={{
                  1: <a href={t("url.deliveryLink")} target="_blank" rel="noreferrer" />,
                }}
              />
            </div>
          )}
        </div>

        <div className={styles.date}>{wishingDateString}</div>

        <LegalAttributes attributes={attributes} />

        {!isActive && <div className={styles.notAvailableNote}>{t("wishlist.itemNoLongerAvailable")}</div>}

        {context && (
          <div className={styles.buttonsWrapper}>
            {isActive && <AddToCartButton itemId={itemId} showQuantity={false} />}

            <button className={`${styles.removeButton}`} onClick={onRemoveClick}>
              <span className={`icon icon-trash--red ${styles.buttonIcon} ${isLoading ? styles.loading : ""}`} />
              <span className={styles.buttonLabel}>{t("wishlist.removeFromList")}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ItemPrice: React.FC<{itmPrice: ICartItemPrice}> = ({itmPrice}) => {
  const {price, retail} = itmPrice;
  const {t} = useTranslation();
  const whitespaceWrapping = (str: string) => str.replace(/\s/g, "\u00A0");

  if (price.amount !== retail.amount) {
    return (
      <div className={styles.priceRetail}>
        <span className={styles.oldPrice}>
          {t("general.rrp--price", {price: whitespaceWrapping(retail.formatted)})}
        </span>{" "}
        <strong>{whitespaceWrapping(price.formatted)}</strong>
      </div>
    );
  }

  return (
    <strong className={styles.itemPriceWrapper}>
      {price.amount === "0.00" ? (
        <span> {t("general.free")}</span>
      ) : (
        <div>
          <span>{whitespaceWrapping(price.formatted)}</span>
        </div>
      )}
    </strong>
  );
};

export default WishlistItem;
